import { useParams, usePathname } from 'next/navigation';
import {
    AnalyticsEventName,
    getClientBrowserParameters,
    sendShopifyAnalytics,
    ShopifyAnalyticsProduct,
    ShopifyPageViewPayload,
    ShopifySalesChannel,
    useShopifyCookies
} from '@shopify/hydrogen-react';
import { getContext } from '@app/lib/store-context/get-context';
import { CurrencyCode } from '@shopify/hydrogen-react/storefront-api-types';
import { Language } from '@app/lib/store-context/Context';

type SendPageViewPayload = {
    pageType?: string;
    products?: ShopifyAnalyticsProduct[];
    collectionHandle?: string;
    searchString?: string;
    totalValue?: number;
    cartId?: string;
};

type SendAddToCartPayload = {
    cartId: string;
    products?: ShopifyAnalyticsProduct[];
    totalValue?: ShopifyPageViewPayload['totalValue'];
};

type SendSearchViewProps = {
    searchTerm: string;
    searchResults: ShopifyAnalyticsProduct[];
}

type SendSearchViewPayload = {
    searchTerm: string;
    search: string;
    searchString: string;
    searchResults: ShopifyAnalyticsProduct[];
}

export function useShopifyAnalytics() {
    const pathname = usePathname();
    const params = useParams();
    const context = getContext(params?.lang as Language);

    if(typeof window !== 'undefined' && typeof (window as any).Shopify !== 'object') {
        (window as any).Shopify = {
            customerPrivacy: {
                analyticsProcessingAllowed: () => true
            }
        }
    }

    const getEventName = () : string => {
        if (pathname.includes('/search/')) {
            return AnalyticsEventName.SEARCH_VIEW;
        }
        if (pathname.includes('/collections/')) {
            return AnalyticsEventName.COLLECTION_VIEW;
        }
        if (pathname.includes('/products/')) {
            return AnalyticsEventName.PRODUCT_VIEW;
        }
        return AnalyticsEventName.PAGE_VIEW;
    }

    const getPageTypeParam = () => {
        if (pathname.includes('/search/')) {
            return 'search';
        }
        if (pathname.includes('/collections/')) {
            return 'collection';
        }
        if (pathname.includes('/products/')) {
            return 'product';
        }
        return 'page';
    }
    
    // Send page view event
    const sendPageView = (eventName: keyof typeof AnalyticsEventName, payload?: SendPageViewPayload | SendSearchViewPayload) => {
        return sendShopifyAnalytics(
            {
                eventName: getEventName(),
                payload: {
                    ...getClientBrowserParameters(),
                    analyticsAllowed: true,
                    hasUserConsent: true,
                    shopifySalesChannel: ShopifySalesChannel.headless,
                    shopId: context.shopifyStoreId,
                    currency: context.currencyCode as CurrencyCode,
                    acceptedLanguage: 'EN',
                    pageType: getPageTypeParam(),
                    ...payload
                }
            }, 
            context.shopifyDomain
        );
    };

    // Send add to cart event
    const sendAddToCart = ({ cartId, totalValue, products }: SendAddToCartPayload) =>
        sendPageView(AnalyticsEventName.ADD_TO_CART, {
            cartId,
            totalValue,
            products
        });

    const sendSearchView = ({ searchTerm, searchResults }: SendSearchViewProps) =>
        sendPageView(AnalyticsEventName.SEARCH_VIEW, {
            searchTerm, 
            searchString: searchTerm,
            search: searchTerm,
            searchResults
        });

    // Set up cookies for Shopify analytics & enable user consent
    useShopifyCookies({
        hasUserConsent: true
    });

    return {
        sendPageView,
        sendAddToCart,
        sendSearchView,
        pathname
    };
}
